import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store';
import { logout } from '../../../store/slices/authSlice';
import * as S from './styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSignOutAlt,
  faSearch,
  faBuilding,
  faBook,
  faUser,
  faClipboardList,
  faChartPie,
  faAddressBook,
  faFileInvoiceDollar,
  faDatabase,
  faMailForward,
  faHeartPulse,
  faClipboardCheck,
  faUserShield,
} from '@fortawesome/free-solid-svg-icons';
import Logo from '../Logo';
import { Link, useLocation } from 'react-router-dom';
import { api } from '../../../services/api';
interface SidebarProps {
  expanded: boolean;
}

interface MenuItem {
  id: string;
  icon: any;
  label: string;
  path: string;
  requiredGroup?: number;
  /** Grupo de acesso restrito: ganha fundo proprio no menu. */
  destaque?: boolean;
  subItems?: MenuItem[];
}

const menuItems: MenuItem[] = [
  {
    id: 'cadastro',
    icon: faBook,
    label: 'Cadastros',
    path: '/cadastros',
    subItems: [
      {
        id: 'cadastro-usuarios',
        icon: faUser,
        label: 'Usuários',
        path: '/cadastros/usuario',
      },
      {
        id: 'cadastro-ocorrencias',
        icon: faFileInvoiceDollar,
        label: 'Ocorrências',
        path: '/cadastros/ocorrencia',
      },
      {
        id: 'cadastro-notas',
        icon: faClipboardList,
        label: 'Notas',
        path: '/cadastros/notas',
      },
    ],
  },
  {
    id: 'relatorios',
    icon: faChartPie,
    label: 'Relatórios',
    path: '/relatorios',
    subItems: [
      {
        id: 'relatorios-entregas',
        icon: faChartPie,
        label: 'Entregas',
        path: '/relatorios/entregas',
      },
      {
        id: 'relatorios-entregadores',
        icon: faMailForward,
        label: 'Entregadores',
        path: '/relatorios/entregadores',
      },
      {
        id: 'relatorios-despesas',
        icon: faFileInvoiceDollar,
        label: 'Despesas',
        path: '/relatorios/despesas',
      },
      {
        id: 'relatorios-conferencia',
        icon: faClipboardCheck,
        label: 'Conferência do dia',
        path: '/relatorios/conferencia',
      },
    ],
  },
  // Telas de Administrador Geral em um bloco proprio: elas expoem a URL do ERP
  // de cada cliente e o estado das chaves de acesso, entao ficam separadas do
  // menu do dia a dia em vez de espalhadas por Cadastros e Relatorios.
  {
    id: 'super-admin',
    icon: faUserShield,
    label: 'Super Admin',
    path: '/super-admin',
    requiredGroup: 1,
    destaque: true,
    subItems: [
      {
        id: 'sa-empresas',
        icon: faBuilding,
        label: 'Empresas',
        path: '/cadastros/empresas',
      },
      {
        id: 'sa-integracao',
        icon: faHeartPulse,
        label: 'Saúde da integração',
        path: '/relatorios/integracao',
      },
      {
        id: 'sa-documentacao',
        icon: faAddressBook,
        label: 'Documentação',
        path: '/documentacao',
      },
    ],
  },
];

const ambientes = [
  'Desenvolvimento',
  'Homologação',
  'Produção'
]


const Sidebar: React.FC<SidebarProps> = ({ expanded }) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { user } = useAppSelector((state) => state.auth);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredMenuItems, setFilteredMenuItems] = useState<MenuItem[]>([]);
  const [empresa, setEmpresa] = useState<string | null>(null);
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>(
    {}
  );
  const [hoveredMenuItem, setHoveredMenuItem] = useState<string | null>(null);
  const [menuItemPosition, setMenuItemPosition] = useState<{
    top: number;
    id: string;
  } | null>(null);

  const availableMenuItems = useMemo(() => {
    // requiredGroup vale tanto para o item de menu quanto para os subitens --
    // antes so o primeiro nivel era filtrado, e um subitem restrito aparecia
    // para todo mundo (a rota e a API barravam, mas o menu mostrava).
    const permitido = (item: MenuItem): boolean =>
      !item.requiredGroup ||
      Boolean(user && user.codigoGrupoUsuario === item.requiredGroup);

    return menuItems.filter(permitido).map((item) =>
      item.subItems
        ? { ...item, subItems: item.subItems.filter(permitido) }
        : item
    );
  }, [user]);

  useEffect(() => {
    setFilteredMenuItems(availableMenuItems);
  }, [availableMenuItems]);

  useEffect(() => setEmpresa(user.empresa), [user]);

  const empresaDisplay = useMemo(() => {
    if (!empresa) return 'Nenhuma empresa selecionada';
    return empresa.charAt(0).toUpperCase() + empresa.slice(1);
  }, [empresa]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredMenuItems(availableMenuItems);
      return;
    }

    const filtered = availableMenuItems.filter(
      (item) =>
        item.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.subItems?.some((subItem) =>
          subItem.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
    );
    setFilteredMenuItems(filtered);
  }, [searchTerm, availableMenuItems]);

  // o bloco restrito sai da lista principal (que estica e empurra o rodape) e
  // vai ancorar junto do Sair
  const itensComuns = filteredMenuItems.filter((item) => !item.destaque);
  const itensRestritos = filteredMenuItems.filter((item) => item.destaque);

  const handleLogout = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  const toggleSubMenu = useCallback((itemId: string) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  }, []);

  const isItemActive = useCallback(
    (item: MenuItem) => {
      if (location.pathname === item.path) return true;
      if (item.subItems) {
        return item.subItems.some(
          (subItem) => location.pathname === subItem.path
        );
      }
      return false;
    },
    [location.pathname]
  );

  useEffect(() => {
    const newExpandedMenus: Record<string, boolean> = {};

    menuItems.forEach((item) => {
      if (
        item.subItems &&
        item.subItems.some((subItem) => location.pathname === subItem.path)
      ) {
        newExpandedMenus[item.id] = true;
      }
    });

    setExpandedMenus((prev) => ({
      ...prev,
      ...newExpandedMenus,
    }));
  }, [location.pathname]);

  const handleMouseEnter = useCallback(
    (itemId: string, event: React.MouseEvent<HTMLLIElement>) => {
      setHoveredMenuItem(itemId);
      const rect = event.currentTarget.getBoundingClientRect();
      setMenuItemPosition({ top: rect.top, id: itemId });
    },
    []
  );

  const handleMouseLeave = useCallback(() => {
    setHoveredMenuItem(null);
    setMenuItemPosition(null);
  }, []);

  // O mesmo desenho serve para a lista principal e para o bloco do rodape --
  // extraido para os dois nao sairem do ar um do outro.
  const renderizaItem = (item: MenuItem) => (
          <React.Fragment key={item.id}>
            <S.MenuItem
              destaque={item.destaque}
              onMouseEnter={(e) =>
                item.subItems && handleMouseEnter(item.id, e)
              }
              onMouseLeave={handleMouseLeave}
              onClick={() =>
                item.subItems && expanded ? toggleSubMenu(item.id) : null
              }
            >
              {item.subItems ? (
                <S.MenuItemButton
                  className={isItemActive(item) ? 'active' : ''}
                >
                  <S.MenuItemIcon>
                    <FontAwesomeIcon icon={item.icon} />
                  </S.MenuItemIcon>
                  {expanded && (
                    <>
                      <S.MenuItemLabel>{item.label}</S.MenuItemLabel>
                      {item.destaque && <S.SeloDestaque>restrito</S.SeloDestaque>}
                      <S.MenuItemArrow
                        expanded={expandedMenus[item.id] || false}
                      />
                    </>
                  )}
                </S.MenuItemButton>
              ) : (
                <S.MenuItemLink
                  as={Link}
                  to={item.path}
                  className={location.pathname === item.path ? 'active' : ''}
                >
                  <S.MenuItemIcon>
                    <FontAwesomeIcon icon={item.icon} />
                  </S.MenuItemIcon>
                  {expanded && <S.MenuItemLabel>{item.label}</S.MenuItemLabel>}
                </S.MenuItemLink>
              )}

              {item.subItems &&
                !expanded &&
                hoveredMenuItem === item.id &&
                menuItemPosition?.id === item.id && (
                  <S.FloatingSubMenu
                    onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) =>
                      handleMouseEnter(
                        item.id,
                        e as unknown as React.MouseEvent<HTMLLIElement>
                      )
                    }
                    onMouseLeave={handleMouseLeave}
                    top={menuItemPosition.top}
                  >
                    {item.subItems.map((subItem) => (
                      <S.SubMenuItem key={subItem.id}>
                        <S.SubMenuItemLink
                          as={Link}
                          to={subItem.path}
                          className={
                            location.pathname === subItem.path ? 'active' : ''
                          }
                        >
                          <S.SubMenuItemIcon>
                            <FontAwesomeIcon icon={subItem.icon} />
                          </S.SubMenuItemIcon>
                          <span>{subItem.label}</span>
                        </S.SubMenuItemLink>
                      </S.SubMenuItem>
                    ))}
                  </S.FloatingSubMenu>
                )}
            </S.MenuItem>

            {item.subItems && expandedMenus[item.id] && expanded && (
              <S.SubMenuList destaque={item.destaque}>
                {item.subItems.map((subItem) => (
                  <S.SubMenuItem key={subItem.id}>
                    <S.SubMenuItemLink
                      as={Link}
                      to={subItem.path}
                      className={
                        location.pathname === subItem.path ? 'active' : ''
                      }
                    >
                      <S.SubMenuItemIcon>
                        <FontAwesomeIcon icon={subItem.icon} />
                      </S.SubMenuItemIcon>
                      <span>{subItem.label}</span>
                    </S.SubMenuItemLink>
                  </S.SubMenuItem>
                ))}
              </S.SubMenuList>
            )}
          </React.Fragment>
  );

  return (
    <S.SidebarContainer expanded={expanded}>
      <S.SidebarHeader>
        <Logo size={expanded ? 'medium' : 'small'} />
      </S.SidebarHeader>

      <S.SidebarBody>
        {expanded && (
          <S.SearchContainer>
            <S.SearchInput
              type="text"
              placeholder="Pesquisar menu"
              value={searchTerm}
              onChange={handleSearch}
            />
            <S.SearchIcon>
              <FontAwesomeIcon icon={faSearch} />
            </S.SearchIcon>
          </S.SearchContainer>
        )}

        <S.MenuList>{itensComuns.map(renderizaItem)}</S.MenuList>

        {/* telas restritas ancoradas no rodape, junto do Sair */}
        {itensRestritos.length > 0 && (
          <S.MenuListRodape>{itensRestritos.map(renderizaItem)}</S.MenuListRodape>
        )}

        <S.LogoutButton onClick={handleLogout}>
          <S.MenuLogoutIcon>
            <FontAwesomeIcon icon={faSignOutAlt} />
          </S.MenuLogoutIcon>
          {expanded && <span>Sair</span>}
        </S.LogoutButton>

        {user.codigoGrupoUsuario == 1 ? (
          <S.DataBase dataBase={api.host.split('://')[1]}>
            <S.DataBaseIcon>
              <FontAwesomeIcon icon={faDatabase} />
            </S.DataBaseIcon>
            {
              expanded &&(
                <>
                {
                api.host.split('://')[1]
                .includes('ada')? ambientes[2] :
                api.host.split('://')[1]
                .includes('altinfo')? ambientes[1] :
                ambientes[0]
                }
                </>
              )
            }
          </S.DataBase>
        ) : null}

        <S.Empresa>
          <S.EmpresaIcon>
            <FontAwesomeIcon icon={faBuilding} />
          </S.EmpresaIcon>
          {expanded && <S.EmpresaText>{empresaDisplay}</S.EmpresaText>}
        </S.Empresa>
      </S.SidebarBody>
    </S.SidebarContainer>
  );
};

export default React.memo(Sidebar);
